/**
 * PATH: apps/qa/src/pages/new-inspection/useNewInspection.ts
 * PURPOSE: Core wizard state, effects, mutation, and computed values
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@quotech/auth'
import { jobQaHubPath, qaRecordPath } from '../records-list/openJobQaInHub'
import api, { inspectionApi } from '@/lib/api'
import { CATEGORY_INFO, TRADE_GROUPS, QUICK_TEMPLATES, FLOOR_TEMPLATES } from '@/lib/inspection-constants'
import type { InspectionStepId, Job, ToastNotification } from './inspection-types'
import { jobFromV2Row } from './jobFromV2Row'
import { analyzeJobForPreselection } from './ai-preselection'
import { useFloorManager } from './useFloorManager'
import { useQuoteSource } from './useQuoteSource'
import { STEPS, stepIdAt, stepIndex } from './inspection-types'

export function useNewInspection() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const organisationId = useAuthStore((s) => s.organisationId)

  // Wizard step. The index drives the progress rail; every branch reads the
  // id so inserting a step cannot renumber a guard (BUG-2026-08-31 class).
  const [currentStep, setCurrentStep] = useState(0)
  const currentStepId: InspectionStepId = stepIdAt(currentStep)
  const [showTemplates, setShowTemplates] = useState(false)

  // Form state
  const [selectedJob, setSelectedJob] = useState<Job | null>(null)
  const [jobSearch, setJobSearch] = useState('')
  const [debouncedJobSearch, setDebouncedJobSearch] = useState('')
  const [inspectionName, setInspectionName] = useState('')
  const [category, setCategory] = useState<'residential' | 'commercial' | 'infrastructure'>('commercial')
  const [projectType, setProjectType] = useState<'new_construction' | 'renovation' | 'remediation'>('new_construction')
  const [buildingClasses, setBuildingClasses] = useState<string[]>([])
  const [tradeTags, setTradeTags] = useState<string[]>([])
  const [customTradeTags, setCustomTradeTags] = useState<string[]>([])
  const [newCustomTag, setNewCustomTag] = useState('')
  const [publishAfterCreate, setPublishAfterCreate] = useState(false)
  const [saveAsTemplate, setSaveAsTemplate] = useState(false)

  // AI tracking
  const [aiSuggestedCategory, setAiSuggestedCategory] = useState<string | null>(null)
  const [aiSuggestedClass, setAiSuggestedClass] = useState<string | null>(null)
  const [aiSuggestedTrades, setAiSuggestedTrades] = useState<string[]>([])
  const [aiConfidence, setAiConfidence] = useState<number>(0)
  const [aiReasoning, setAiReasoning] = useState<string>('')

  // Manual selection tracking
  const [, setHasManualTradeSelection] = useState(false)
  const [hasManualClassSelection, setHasManualClassSelection] = useState(false)
  const prevCategoryRef = useRef(category)

  // Floor manager
  const floorMgr = useFloorManager()

  // Optional accepted-quote source for this inspection
  const quoteSource = useQuoteSource(selectedJob, organisationId)

  const [newJobOpen, setNewJobOpen] = useState(false)

  // Toasts
  const [toasts, setToasts] = useState<ToastNotification[]>([])
  const showToast = useCallback((toast: Omit<ToastNotification, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9)
    setToasts(prev => [...prev, { ...toast, id }])
  }, [])
  const dismissToast = useCallback((id: string) => setToasts(prev => prev.filter(t => t.id !== id)), [])

  // Refs
  const customTagInputRef = useRef<HTMLInputElement>(null)

  // Debounce search
  useEffect(() => { const t = setTimeout(() => setDebouncedJobSearch(jobSearch), 300); return () => clearTimeout(t) }, [jobSearch])

  // Category change → update class
  const suggestedClasses = CATEGORY_INFO[category]?.suggestedClasses || []
  const suggestedTrades = CATEGORY_INFO[category]?.suggestedTrades || []
  useEffect(() => {
    const changed = prevCategoryRef.current !== category; prevCategoryRef.current = category
    if (changed && selectedJob && !hasManualClassSelection && suggestedClasses.length > 0) setBuildingClasses([suggestedClasses[0]])
  }, [category, suggestedClasses, hasManualClassSelection, selectedJob])

  // Fetch jobs
  // Reused from: packages/job-hub/src/jobs-list/gateway/platformJobsListGateway.ts (searchSuggestions: same endpoint and params)
  const jobsQuery = useInfiniteQuery({
    queryKey: ['jobs-list', debouncedJobSearch, organisationId],
    queryFn: async ({ pageParam }) => {
      if (!organisationId) return { results: [] as Job[], next: null as string | null }
      const params: Record<string, string> = {
        page_size: '50',
        tenant: String(organisationId),
        page: String(pageParam),
      }
      if (debouncedJobSearch) params.search = debouncedJobSearch
      const payload = (await api().get('/v2/jobs/', { params })).data as {
        results?: Record<string, unknown>[]
        next?: string | null
      }
      const results = Array.isArray(payload?.results) ? payload.results.map(jobFromV2Row) : []
      return { results, next: payload?.next ?? null }
    },
    initialPageParam: 1,
    getNextPageParam: (last, _pages, lastPageParam) => (last.next ? Number(lastPageParam) + 1 : undefined),
    enabled: !!organisationId,
  })
  const jobs: Job[] = jobsQuery.data?.pages.flatMap((page) => page.results) ?? []
  const jobsLoading = jobsQuery.isLoading
  const hasNextJobs = Boolean(jobsQuery.hasNextPage)
  const jobsFetchingNext = jobsQuery.isFetchingNextPage
  const fetchNextJobs = jobsQuery.fetchNextPage

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async () => {
      // From-quote path: the server derives floors, areas and checklist items
      // from the immutable quote scope, so none of the manual build below runs.
      if (quoteSource.hasQuoteSource) {
        const generated = await quoteSource.createFromQuotes(inspectionName)
        if (publishAfterCreate) { try { await inspectionApi.publishInspection(generated.id) } catch {} }
        return generated
      }
      const inspection = await inspectionApi.createInspection({ job: selectedJob?.job_id || '', inspection_name: inspectionName, category, project_type: projectType, building_classes: buildingClasses, trade_tags: tradeTags })
      if (!inspection?.id) throw new Error('Inspection created but no ID returned')
      for (let i = 0; i < floorMgr.floors.length; i++) {
        const fd = floorMgr.floors[i]
        try {
          const fr = await api().post('/qa/floors/', { inspection: inspection.id, floor_name: fd.floor_name, sort_order: i })
          const fid = fr.data.id
          for (const ad of fd.areas) {
            try {
              const ar = await api().post('/qa/areas/', { floor: fid, area_name: ad.area_name, area_code: ad.area_code })
              const aid = ar.data.id
              if (ad.notes?.trim() && aid) { try { await api().post('/qa/evidence/', { area: aid, type: 'note', note_text: ad.notes.trim(), folder: 'Setup Notes' }) } catch {} }
              for (const att of ad.attachments || []) {
                try {
                  const form = new FormData(); form.append('file', att.file); form.append('purpose', 'qa_evidence')
                  const ur = await api().post('/files/upload/', form, { headers: { 'Content-Type': 'multipart/form-data' } })
                  await api().post('/qa/evidence/', { area: aid, type: att.type, asset_id: ur.data.id, description: `Setup: ${att.name}`, folder: 'Setup Attachments' })
                } catch {}
              }
            } catch (err) { console.error(`[QA] Area "${ad.area_name}" failed:`, err) }
          }
          if (fd.notes?.trim() && fid) { try { await api().post('/qa/evidence/', { floor: fid, type: 'note', note_text: fd.notes.trim(), folder: 'Setup Notes' }) } catch {} }
        } catch (err) { console.error(`[QA] Floor "${fd.floor_name}" failed:`, err) }
      }
      if (publishAfterCreate) { try { await inspectionApi.publishInspection(inspection.id) } catch {} }
      return inspection
    },
    onSuccess: (insp) => { queryClient.invalidateQueries({ queryKey: ['qa-inspections'] }); navigate(`/records/${insp.id}`) },
    onError: (err) => console.error('[QA] Create failed:', err),
  })

  // Computed
  const validationWarnings = useMemo(() => {
    const w: string[] = []
    if (!selectedJob) w.push('No job selected')
    if (!inspectionName.trim()) w.push('Inspection name is required')
    if (buildingClasses.length === 0) w.push('No building classes selected')
    if (tradeTags.length === 0) w.push('No trade tags selected')
    if (floorMgr.floors.length === 0) w.push('No floors added - you can add later')
    const empty = floorMgr.floors.filter(f => f.areas.length === 0)
    if (empty.length > 0) w.push(`${empty.length} floor(s) have no areas`)
    return w
  }, [selectedJob, inspectionName, buildingClasses, tradeTags, floorMgr.floors])

  const completionProgress = useMemo(() => {
    let s = 0; if (selectedJob) s++; if (inspectionName.trim()) s++; if (buildingClasses.length > 0) s++; if (tradeTags.length > 0) s++; if (floorMgr.floors.length > 0) s++
    return Math.round((s / 5) * 100)
  }, [selectedJob, inspectionName, buildingClasses, tradeTags, floorMgr.floors])

  // Navigation
  const canProceed = () => { switch (currentStepId) { case 'job': return !!selectedJob; case 'details': return inspectionName.trim().length > 0; default: return true } }
  const stepPastDetails = (from: number, dir: 1 | -1) => {
    let next = from + dir
    while (next >= 0 && next < STEPS.length && STEPS[next].id === 'details') next += dir
    return next
  }
  const goNext = () => {
    if (!canProceed()) return
    const next = stepPastDetails(currentStep, 1)
    if (next < STEPS.length) setCurrentStep(next)
  }
  const goBack = () => {
    if (currentStepId === STEPS[0].id) return
    if (createMutation.isError) createMutation.reset()
    const prev = stepPastDetails(currentStep, -1)
    if (prev >= 0) setCurrentStep(prev)
  }
  const goToStep = (s: number) => { if (s < currentStep) setCurrentStep(s) }
  const goToStepId = (id: InspectionStepId) => goToStep(stepIndex(id))

  // Trades
  const toggleTradeTag = (tag: string) => { setHasManualTradeSelection(true); setTradeTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]) }
  const addCustomTag = () => { const t = newCustomTag.trim(); if (!t || customTradeTags.includes(t) || tradeTags.includes(t)) return; setCustomTradeTags(prev => [...prev, t]); setTradeTags(prev => [...prev, t]); setNewCustomTag('') }
  const removeCustomTag = (tag: string) => { setCustomTradeTags(prev => prev.filter(t => t !== tag)); setTradeTags(prev => prev.filter(t => t !== tag)) }

  // Job selection handler
  const handleSelectJob = useCallback((job: Job) => {
    setSelectedJob(job)
    if (!inspectionName) {
      setInspectionName(
        job.display_name
          ? `${job.display_name} - QA Inspection`
          : `Job #${job.job_id} - QA Inspection`,
      )
    }
    const analysis = analyzeJobForPreselection(job, null)
    setCategory(analysis.category); setProjectType(analysis.projectType)
    if (analysis.buildingClass) setBuildingClasses([analysis.buildingClass])
    setTradeTags(analysis.tradeTags)
    setAiSuggestedCategory(analysis.category); setAiSuggestedClass(analysis.buildingClass); setAiSuggestedTrades(analysis.tradeTags); setAiConfidence(analysis.confidence); setAiReasoning(analysis.reason)
    setHasManualTradeSelection(false); setHasManualClassSelection(false)
    showToast({ type: 'info', message: `AI pre-selected: ${analysis.reason}` })
    setCurrentStep(stepIndex('source'))
  }, [inspectionName, showToast])

  const handleJobCreated = useCallback((job: Job) => {
    queryClient.invalidateQueries({ queryKey: ['jobs-list'] })
    const desktop = typeof window !== 'undefined'
      && window.matchMedia('(min-width: 768px)').matches
    if (!desktop) {
      handleSelectJob(job)
      return
    }
    const name = job.display_name?.trim()
      ? `${job.display_name.trim()} - QA Inspection`
      : `Job #${job.job_id} - QA Inspection`
    void inspectionApi.createInspection({
      // Reused from: apps/qa/src/pages/new-inspection/useNewInspection.ts (createMutation / inspectionApi.createInspection)
      job: String(job.job_id),
      inspection_name: name,
      category: 'commercial',
      project_type: 'new_construction',
      building_classes: [],
      trade_tags: [],
    }).then((created) => {
      navigate(created?.id ? qaRecordPath(created.id) : jobQaHubPath(String(job.job_id)))
    }).catch((err: unknown) => {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Could not create the inspection',
      })
    })
  }, [handleSelectJob, navigate, queryClient, showToast])

  // Template
  const applyQuickTemplate = useCallback((tmpl: typeof QUICK_TEMPLATES[0]) => {
    setCategory(tmpl.category); setProjectType(tmpl.projectType); setBuildingClasses(tmpl.buildingClasses); setTradeTags(tmpl.trades)
    floorMgr.applyTemplate(tmpl); setShowTemplates(false)
    if (!inspectionName && selectedJob) setInspectionName(`Job #${selectedJob.job_id} - ${tmpl.name}`)
  }, [inspectionName, selectedJob, floorMgr])

  return {
    navigate, currentStep, currentStepId, showTemplates, setShowTemplates,
    selectedJob, setSelectedJob, jobSearch, setJobSearch, jobs, jobsLoading,
    inspectionName, setInspectionName, category, setCategory, projectType, setProjectType,
    buildingClasses, setBuildingClasses, tradeTags, setTradeTags,
    customTradeTags, newCustomTag, setNewCustomTag, customTagInputRef,
    publishAfterCreate, setPublishAfterCreate, saveAsTemplate, setSaveAsTemplate,
    aiSuggestedCategory, aiSuggestedClass, aiSuggestedTrades, aiConfidence, aiReasoning,
    hasManualClassSelection, setHasManualClassSelection, suggestedClasses, suggestedTrades,
    floorMgr, quoteSource, toasts, showToast, dismissToast, createMutation,
    validationWarnings, completionProgress,
    canProceed, goNext, goBack, goToStep, goToStepId,
    toggleTradeTag, addCustomTag, removeCustomTag, handleSelectJob, applyQuickTemplate,
    newJobOpen, setNewJobOpen, handleJobCreated, organisationId,
    hasNextJobs, jobsFetchingNext, fetchNextJobs,
  }
}
